import { Row, Col, Card } from 'antd';
import { UserOutlined, DollarOutlined } from '@ant-design/icons';
import { BRAND, formatCompactCurrency } from '../constants';
import TrendLine from './TrendLine';
import StatusDonut, { DonutSlice } from './StatusDonut';

interface StatsCardsProps {
    animatedUsers: number;
    animatedAmount: number;
    trendPoints: number[];
    statusSlices: DonutSlice[];
}

// Three stat cards — Transacting Users / Total Outstanding (highlighted) / Status Breakdown.
export default function StatsCards({ animatedUsers, animatedAmount, trendPoints, statusSlices }: StatsCardsProps) {
    return (
        <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
            {/* 1 · Transacting Users (white) */}
            <Col xs={24} md={8}>
                <Card
                    className="insight-donut"
                    style={{
                        background: '#fff',
                        border: '1px solid #E7EEEF',
                        borderRadius: '16px',
                        boxShadow: '0 4px 14px rgba(13, 82, 87, 0.06)',
                        height: '100%',
                    }}
                    bodyStyle={{ padding: '18px 20px', height: '100%' }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', minHeight: 118 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                            <div>
                                <span style={{ fontSize: 11, color: '#7c8b91', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    Transacting Users
                                </span>
                                <div style={{ fontSize: 34, fontWeight: 800, color: BRAND.ink, lineHeight: 1.05, marginTop: 8 }}>
                                    {animatedUsers}
                                </div>
                            </div>
                            <div style={{
                                width: 46, height: 46, borderRadius: 14, flexShrink: 0,
                                background: BRAND.tealSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <UserOutlined style={{ fontSize: 22, color: BRAND.teal }} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
                            <span style={{ position: 'relative', display: 'inline-flex', width: 8, height: 8 }}>
                                <span style={{
                                    position: 'absolute', inset: 0, borderRadius: '50%',
                                    background: BRAND.green, animation: 'livePulse 2s ease-in-out infinite',
                                }} />
                                <span style={{ position: 'relative', width: 8, height: 8, borderRadius: '50%', background: BRAND.green }} />
                            </span>
                            <span style={{ fontSize: 11.5, color: '#8a979c', fontWeight: 500 }}>
                                Live · synced with latest data
                            </span>
                        </div>
                    </div>
                </Card>
            </Col>

            {/* 2 · Total Outstanding (highlighted dark-teal) */}
            <Col xs={24} md={8}>
                <Card
                    className="insight-hero"
                    style={{
                        background: BRAND.heroGradient,
                        border: 'none',
                        borderRadius: '16px',
                        boxShadow: '0 10px 26px rgba(13, 82, 87, 0.26)',
                        height: '100%',
                        overflow: 'hidden',
                    }}
                    bodyStyle={{ padding: '18px 20px', height: '100%' }}
                >
                    {/* decorative corner glow */}
                    <div style={{
                        position: 'absolute', top: -40, right: -30, width: 130, height: 130,
                        borderRadius: '50%', background: 'rgba(255,255,255,0.10)', pointerEvents: 'none',
                    }} />
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', minHeight: 118 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                            <div>
                                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    Total Outstanding
                                </span>
                                <div style={{ fontSize: 30, fontWeight: 800, color: '#fff', lineHeight: 1.1, marginTop: 8 }}>
                                    {formatCompactCurrency(animatedAmount)}
                                </div>
                            </div>
                            <div style={{
                                width: 46, height: 46, borderRadius: 14, flexShrink: 0,
                                background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.22)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <DollarOutlined style={{ fontSize: 22, color: '#fff' }} />
                            </div>
                        </div>
                        <div style={{ marginTop: 14, marginInline: -4 }}>
                            <TrendLine points={trendPoints} stroke={BRAND.yellow} width={240} height={40} />
                        </div>
                    </div>
                </Card>
            </Col>

            {/* 3 · Status Breakdown (white) */}
            <Col xs={24} md={8}>
                <Card
                    className="insight-donut"
                    style={{
                        background: '#fff',
                        border: '1px solid #E7EEEF',
                        borderRadius: '14px',
                        boxShadow: '0 4px 14px rgba(13, 82, 87, 0.06)',
                        height: '100%',
                    }}
                    bodyStyle={{ padding: '14px 16px' }}
                >
                    <div style={{ fontSize: 12, color: BRAND.ink, fontWeight: 700, marginBottom: 10 }}>
                        Status Breakdown
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <StatusDonut slices={statusSlices} size={90} thickness={14} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
                            {statusSlices.length === 0 && (
                                <span style={{ fontSize: 11, color: '#98a7ac' }}>No data</span>
                            )}
                            {statusSlices.slice(0, 5).map((s, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ width: 9, height: 9, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                                    <span style={{ fontSize: 11, color: '#5b6b70', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {s.label}
                                    </span>
                                    <span style={{ fontSize: 11, color: BRAND.ink, fontWeight: 700 }}>{s.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>
            </Col>
        </Row>
    );
}
